import React, { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { collection, query, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { CompiledMasterLog } from '../../types';
import { Archive, FileText, Download, ExternalLink, BookOpen } from 'lucide-react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

interface HistoricLogItem {
  id: string;
  title: string;
  stardate: string;
  pdfUrl: string;
  type: 'pdf' | 'html';
  compiledData?: CompiledMasterLog;
}

const HISTORIC_PDF_LOGS: HistoricLogItem[] = [
  {
    id: '2026-04',
    title: 'USS Zavala Monthly Log - April 2026',
    stardate: '2026.04',
    pdfUrl: 'historic_logs/USS-ZAVALA-LOG-2026-04.pdf',
    type: 'pdf'
  },
  {
    id: '2026-03',
    title: 'USS Zavala Monthly Log - March 2026',
    stardate: '2026.03',
    pdfUrl: 'historic_logs/USS-ZAVALA-LOG-2026-03.pdf',
    type: 'pdf'
  }
];

export default function ArchiveViewer() {
  const [loading, setLoading] = useState(true);
  const [allLogs, setAllLogs] = useState<HistoricLogItem[]>(HISTORIC_PDF_LOGS);
  const [selectedLog, setSelectedLog] = useState<HistoricLogItem | null>(HISTORIC_PDF_LOGS[0] || null);

  useEffect(() => {
    const loadArchives = async () => {
      try {
        const q = query(collection(db, 'compiledReports'), orderBy('id', 'desc'));
        const snapshot = await getDocs(q);
        const compiledData = snapshot.docs.map(doc => {
          const data = doc.data() as CompiledMasterLog;
          return {
            id: data.id,
            title: `USS Zavala Master Log - ${data.id}`,
            stardate: data.id,
            pdfUrl: '',
            type: 'html' as const,
            compiledData: data
          };
        });

        // Merge compiled data with historic PDF logs (avoiding duplicates if id matches)
        const combined = [...HISTORIC_PDF_LOGS];
        compiledData.forEach(item => {
          if (!combined.some(existing => existing.id === item.id)) {
            combined.unshift(item);
          }
        });

        // Sort by ID descending (most recent first)
        combined.sort((a, b) => b.id.localeCompare(a.id));

        setAllLogs(combined);
        if (combined.length > 0) {
          setSelectedLog(combined[0]);
        }
      } catch (err) {
        console.error("Failed to load archives from Firestore", err);
        // Fallback to historic PDF logs
        setAllLogs(HISTORIC_PDF_LOGS);
        setSelectedLog(HISTORIC_PDF_LOGS[0]);
      }
      setLoading(false);
    };

    loadArchives();
  }, []);

  const handleDownloadCompiledPdf = async (archive: CompiledMasterLog) => {
    const rawContent = archive.versions.find(v => v.versionId === archive.currentVersionId)?.content || '';
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = DOMPurify.sanitize(rawContent);
    tempDiv.className = 'bg-[var(--background)] text-[var(--foreground)] p-8 w-[800px] absolute -left-[9999px]';
    document.body.appendChild(tempDiv);

    try {
      const canvas = await html2canvas(tempDiv, { scale: 2, useCORS: true, backgroundColor: '#000000' });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      
      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      pdf.save(`USS_Zavala_MasterLog_${archive.id}.pdf`);
    } catch (e) {
      console.error("Failed to generate PDF", e);
    } finally {
      document.body.removeChild(tempDiv);
    }
  };

  if (loading) {
    return <div className="p-8 text-lcars-blue animate-pulse font-bold tracking-widest uppercase">ACCESSING SHIP'S LOG ARCHIVES...</div>;
  }

  return (
    <div className="flex h-full flex-col md:flex-row gap-4 p-2 md:p-4">
      {/* Sidebar List */}
      <div className="w-full md:w-80 flex flex-col gap-2 overflow-y-auto shrink-0">
        <div className="bg-[var(--background)] border-l-4 border-lcars-blue pl-3 py-1 mb-2">
          <h2 className="text-lg font-bold text-lcars-blue flex items-center gap-2 uppercase tracking-wider">
            <Archive className="w-5 h-5 text-lcars-gold" />
            Ship's Log Matrix
          </h2>
          <p className="text-[0.65rem] text-[var(--foreground)]/50 uppercase font-mono">
            {allLogs.length} Official Vessel Records
          </p>
        </div>
        
        <div className="space-y-2">
          {allLogs.map(log => {
            const isSelected = selectedLog?.id === log.id;
            return (
              <button
                key={log.id}
                onClick={() => setSelectedLog(log)}
                className={`w-full flex items-center justify-between p-3 rounded-xl border text-left transition-all ${
                  isSelected
                    ? 'bg-lcars-blue text-black border-lcars-blue shadow-lg font-bold'
                    : 'bg-black/40 border-white/10 text-[var(--foreground)] hover:border-lcars-blue/50 hover:bg-white/5'
                }`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <BookOpen className={`w-5 h-5 shrink-0 ${isSelected ? 'text-black' : 'text-lcars-gold'}`} />
                  <div className="truncate">
                    <p className="font-bold text-sm tracking-wide truncate">{log.title}</p>
                    <p className={`text-[0.65rem] font-mono ${isSelected ? 'text-black/70' : 'text-[var(--foreground)]/40'}`}>
                      Stardate {log.stardate} • {log.type === 'pdf' ? 'PDF Edition' : 'Digital Log'}
                    </p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Main Viewer Area */}
      <div className="flex-1 bg-black/40 border-2 border-lcars-blue rounded-xl p-4 md:p-6 overflow-y-auto flex flex-col min-h-[500px]">
        {selectedLog ? (
          <div className="flex-1 flex flex-col">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center border-b border-white/10 pb-4 mb-4 gap-3">
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-lcars-gold uppercase tracking-tight">
                  {selectedLog.title}
                </h2>
                <p className="text-xs text-lcars-tan font-mono uppercase tracking-widest mt-0.5">
                  Official Record • Stardate {selectedLog.stardate}
                </p>
              </div>

              <div className="flex items-center gap-2">
                {selectedLog.type === 'pdf' ? (
                  <a
                    href={`${import.meta.env.BASE_URL}${selectedLog.pdfUrl}`}
                    download
                    className="flex items-center gap-2 px-4 py-2 bg-lcars-gold text-black rounded-full hover:bg-lcars-gold/80 font-bold text-xs uppercase tracking-wider transition-colors"
                  >
                    <Download className="w-4 h-4" />
                    Download PDF
                  </a>
                ) : selectedLog.compiledData ? (
                  <button
                    onClick={() => handleDownloadCompiledPdf(selectedLog.compiledData!)}
                    className="flex items-center gap-2 px-4 py-2 bg-lcars-blue text-black rounded-full hover:bg-lcars-blue/80 font-bold text-xs uppercase tracking-wider transition-colors"
                  >
                    <Download className="w-4 h-4" />
                    Export PDF
                  </button>
                ) : null}

                {selectedLog.type === 'pdf' && (
                  <a
                    href={`${import.meta.env.BASE_URL}${selectedLog.pdfUrl}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 px-3 py-2 bg-white/10 text-[var(--foreground)] rounded-full hover:bg-white/20 text-xs font-bold uppercase tracking-wider"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    Open Tab
                  </a>
                )}
              </div>
            </div>

            {selectedLog.type === 'pdf' ? (
              <div className="flex-1 min-h-[600px] border border-white/10 rounded-lg overflow-hidden bg-black/60 flex flex-col">
                <iframe
                  src={`${import.meta.env.BASE_URL}${selectedLog.pdfUrl}#toolbar=1&navpanes=0`}
                  title={selectedLog.title}
                  className="w-full flex-1 border-0 min-h-[600px]"
                />
              </div>
            ) : selectedLog.compiledData ? (
              <div 
                className="prose prose-invert max-w-none text-[var(--foreground)]"
                dangerouslySetInnerHTML={{ 
                  __html: DOMPurify.sanitize(selectedLog.compiledData.versions.find(v => v.versionId === selectedLog.compiledData!.currentVersionId)?.content || 'No content recorded.') 
                }} 
              />
            ) : (
              <div className="flex-1 flex items-center justify-center text-[var(--foreground)]/40 font-bold uppercase tracking-widest">
                Log record content unavailable.
              </div>
            )}
          </div>
        ) : (
          <div className="h-full flex items-center justify-center text-[var(--foreground)]/30 font-bold tracking-widest uppercase">
            SELECT A SHIP'S LOG RECORD TO VIEW
          </div>
        )}
      </div>
    </div>
  );
}
